import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../config/supabase';

const VendorsContext = createContext();

export const useVendors = () => {
  const context = useContext(VendorsContext);
  if (!context) {
    throw new Error('useVendors must be used within a VendorsProvider');
  }
  return context;
};

export const VendorsProvider = ({ children }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load vendors from Supabase on mount
  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .order('priority', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading vendrs:', error);
        return;
      }

      setVendors(data || []);
    } catch (error) {
      console.error('Error loading vendrs:', error);
    } finally {
      setLoading(false);
    }
  };

  const createVendor = async (vendorData) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .insert([vendorData])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      setVendors([data, ...vendors]);
      setLoading(false);
      return { success: true, data };
    } catch (error) {
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  const updateVendor = async (id, vendorData) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .update(vendorData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      setVendors(vendors.map((v) => (v.id === id ? data : v)));
      setLoading(false);
      return { success: true, data };
    } catch (error) {
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  const deleteVendor = async (id) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', id);

      if (error) {
        return { success: false, error: error.message };
      }

      setVendors(vendors.filter((v) => v.id !== id));
      setLoading(false);
      return { success: true };
    } catch (error) {
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  const updateVendorsPriority = async (reorderedVendors) => {
    try {
      // Update priorities for all vendrs
      const updates = reorderedVendors.map((vendor, index) => ({
        id: vendor.id,
        priority: index + 1,
      }));

      // Update each vendr's priority in the database
      for (const update of updates) {
        const { error } = await supabase
          .from('vendors')
          .update({ priority: update.priority })
          .eq('id', update.id);

        if (error) {
          console.error('Error updating priority:', error);
          return { success: false, error: error.message };
        }
      }

      // Update local state
      setVendors(reorderedVendors);
      return { success: true };
    } catch (error) {
      console.error('Error updating priorities:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    vendors,
    loading,
    createVendor,
    updateVendor,
    deleteVendor,
    updateVendorsPriority,
    refreshVendors: loadVendors,
  };

  return <VendorsContext.Provider value={value}>{children}</VendorsContext.Provider>;
};

